import type { components } from './schema';

/**
 * The vocabularies the OpenAPI document carries, **derived** from it rather than restated.
 *
 * `components.schemas.ConsiderationStatus` has been in the document since Sprint 3 (TASK-020) and
 * `components.schemas.OverallRecommendation` since Sprint 4 (TASK-030), so both were hand-written
 * unions in `StatusDot.tsx` for no reason at all — and `api.test.ts` exempted them from its
 * hand-written-enum guard on the recorded ground that *"the OpenAPI document does not carry"* them,
 * which was false for both. The exemption is gone with them (TASK-061).
 *
 * `Recommendation` keeps the shorter name the screens read with; it is the same set of four values
 * the backend calls `OverallRecommendation`.
 */
export type ConsiderationStatus = components['schemas']['ConsiderationStatus'];
export type Recommendation = components['schemas']['OverallRecommendation'];

/**
 * §6.9's two question vocabularies, derived the same way (TASK-070).
 *
 * ⚠️ **The stored value is a token and the label is a sentence, and the two differ on purpose.**
 * `Category`'s own docstring in the document says it: the glossary spells the third one *"SYSTEM
 * DESIGN"* with a space, and a space is awkward in a URL, a query parameter and a Python identifier,
 * so the stored constant is `SYSTEM_DESIGN` while the human label stays *"System design"*
 * (`backend/CONTEXT.md`). No screen may render the token — the labels live in the catalogue
 * (`i18n/en/components.json`, beside `status` and `recommendation`) and `vacancies.test.tsx` asserts
 * its keys **equal** these enums, so a value added on the backend fails the suite — **with the snapshot
 * refreshed** (BUG-070) — instead of putting `SYSTEM_DESIGN` in front of a person. The assertion it
 * names already says so; this half said it without the qualifier until 2026-08-09.
 */
export type Category = components['schemas']['Category'];
export type Difficulty = components['schemas']['Difficulty'];

/**
 * §6.8's requisition status, derived the same way since the document declared it (TASK-179).
 *
 * ⚠️ **`pages/Vacancies/requisition.ts` spelled these five tokens out by hand until 2026-10-29, and
 * that is retracted rather than tidied.** The reason it gave was true when it was written —
 * `RoleOut.requisition_status` was a bare `str`, so the document carried no
 * `components.schemas.RequisitionStatus` to derive from — and it named the fix as a **backend** item,
 * which is the one that shipped: the column is declared as its `TextChoices`, the document carries the
 * enum, and the list below is the document's own. It is the same correction `ConsiderationStatus` and
 * `OverallRecommendation` each had before it, and `PipelineSegment.status` is now the only field left
 * in that position.
 *
 * **The empty string is a value of the field and is deliberately not a member here.** `RoleOut`
 * types it `RequisitionStatus | ''`, and `''` means *"not a requisition"* — every vacancy Panel's own
 * screens created, which is every vacancy that existed before Sprint 24. A screen asks whether a row
 * **has** a status; this enum is what the five words are.
 */
export type RequisitionStatus = components['schemas']['RequisitionStatus'];

/**
 * The runtime lists, kept derived by construction: a `Record` over the type must carry a key for
 * every member and may carry no other, so a value added to or removed from the backend's enum stops
 * `tsc` **here** rather than reaching a `<Select>` or a style table. This is the same shape
 * `dashboard.ts` uses for `?period=`, and `candidates.test.tsx` compares both lists against the
 * enums in `openapi.snapshot.json` so the values are checked against the document too, not only
 * against the literal written beside them.
 *
 * The order is `DESIGN.md` §3's order, which is also the document's.
 */
const CONSIDERATION_STATUS: Record<ConsiderationStatus, true> = {
  NEW: true,
  INTERVIEWING: true,
  OFFER: true,
  HIRED: true,
  REJECTED: true,
};

export const CONSIDERATION_STATUSES = Object.keys(CONSIDERATION_STATUS) as ConsiderationStatus[];

const RECOMMENDATION: Record<Recommendation, true> = {
  STRONG_YES: true,
  YES: true,
  NO: true,
  STRONG_NO: true,
};

export const RECOMMENDATIONS = Object.keys(RECOMMENDATION) as Recommendation[];

const CATEGORY: Record<Category, true> = {
  TECHNICAL: true,
  BEHAVIORAL: true,
  SYSTEM_DESIGN: true,
};

export const CATEGORIES = Object.keys(CATEGORY) as Category[];

const DIFFICULTY: Record<Difficulty, true> = {
  EASY: true,
  MEDIUM: true,
  HARD: true,
};

export const DIFFICULTIES = Object.keys(DIFFICULTY) as Difficulty[];

/**
 * Whether a value is one of the three categories, and one of the three difficulties.
 *
 * ⚠️ **Written by TASK-073, not TASK-070, and the note that stood here said why**: their one caller is
 * §6.9's address-bar parser (`readQuestionFilters` in `pages/Vacancies/vacancyUrls.ts`), which must
 * **drop** a value it does not recognise rather than send it — `?category=TECHNICALL` is a 422 from
 * django-ninja, and a shortened or mistyped address must not become §8's error state (§6.3's rule).
 * Until that parser existed these would have been two exports nothing called, which is Sprint 8's
 * `applyTenantAccent()`.
 *
 * The lists come from the OpenAPI document (above), so a value added on the backend is admitted here
 * without an edit — and a value removed stops `tsc` at the `Record` rather than at a `<Select>`.
 */
export function isCategory(value: string): value is Category {
  return value in CATEGORY;
}

export function isDifficulty(value: string): value is Difficulty {
  return value in DIFFICULTY;
}

/** Whether a value is one of the four verdicts — the branch `StatusDot` takes to pick its label. */
export function isRecommendation(value: string): value is Recommendation {
  return value in RECOMMENDATION;
}

/**
 * Whether a value is one of the five statuses.
 *
 * The list comes from the OpenAPI document, so a status added on the backend is admitted here without
 * an edit. It lives beside `isRecommendation` rather than in the screen that first needed it
 * (`candidateFilters.ts`, which reads it out of the address bar) because two callers now ask the same
 * question of two different sources — a hand-typed URL and a dashboard payload — and one guard
 * derived from the schema is the whole point.
 */
export function isConsiderationStatus(value: string): value is ConsiderationStatus {
  return value in CONSIDERATION_STATUS;
}

/**
 * ⚠️ **The one narrowing this layer still owes a screen, and the dashboard is why it exists.**
 *
 * `PipelineSegment.status` is the last field the document types as a plain `string` where the values
 * are a vocabulary, and §6.2 Section 3 renders it through `StatusDot` — where a value with no style
 * table entry makes `style.pill` a read off `undefined`, which throws and takes the **whole
 * dashboard** with it, not just one segment. TASK-061 found the two casts
 * (`segment.status as ConsiderationStatus`) and left the narrowing to the task that owns the section;
 * TASK-068 is it. Typing that field as its enum on the backend is still the first line of defence and
 * still a Backlog item; until it ships, this is where an unknown code stops.
 *
 * ⚠️ **A verdict twin, `asRecommendation`, stood here until BUG-038 and is gone.** `VerdictCount.verdict`
 * is the declared `OverallRecommendation` in the document since backend TASK-104, and so are
 * `latest_recommendation` and `overall_recommendation` — the four screens that narrowed them at this
 * edge stopped calling it in the same sprint, and an exported helper with no caller is what Sprint 8
 * shipped as `applyTenantAccent()` and what `code-reviewer` refused twice. Anything here that reads as
 * *"narrowed once, at the API edge"* about a **verdict** is describing a guarantee the document now
 * makes itself; the guarantee for a **status** is this function.
 *
 * A value outside the five is **nothing**: see `dashboard.ts` for what a section then does with it,
 * which is the caller's decision and not this function's.
 */
export function asConsiderationStatus(
  value: string | null | undefined,
): ConsiderationStatus | null {
  if (!value) return null;
  return isConsiderationStatus(value) ? value : null;
}
